import { encryptMD5 } from './MD5';

export const STORAGE_V2_PREFIX = 'hzc:v2:';

export function v2StorageKey(logicalKey: string): string {
  return `${STORAGE_V2_PREFIX}${logicalKey}`;
}

/** Same hex as historical crypto-js MD5(logicalKey). Native cookie names stay on this. */
export function legacyMd5Key(logicalKey: string): string {
  return encryptMD5(logicalKey);
}

type Store = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
  removeItem(key: string): unknown;
};

/**
 * Dual-read / dual-write for browser localStorage only.
 *
 * - New key: `hzc:v2:${logicalKey}` (readable, drop-in for a later crypto-js removal)
 * - Legacy key: MD5(logicalKey) so existing baskets / city / user survive
 * - Values stay AES OpenSSL ciphertext (or plaintext JSON where they already were)
 *
 * Do not use this for:
 * - Native WebView cookies (MD5 names — iOS/Android look those up)
 * - `hzc_token` / `hzc_device_id` (already stable cookie names)
 * - `hzcActiveUser` (GTM/logging plaintext)
 * - UTM / gclid (`persistCampaignParams` is memory-only on purpose)
 */
export function readMigratedItem(store: Store, logicalKey: string): string | null {
  const nextKey = v2StorageKey(logicalKey);
  const nextVal = store.getItem(nextKey);
  if (nextVal != null) return nextVal;

  const legacyKey = legacyMd5Key(logicalKey);
  const legacyVal = store.getItem(legacyKey);
  if (legacyVal == null) return null;
  try {
    store.setItem(nextKey, legacyVal);
  } catch {
    // quota / private mode — keep serving the legacy value
  }
  return legacyVal;
}

export function writeMigratedItem(store: Store, logicalKey: string, value: string): void {
  // Legacy first so a rollback build still sees the latest basket / user.
  store.setItem(legacyMd5Key(logicalKey), value);
  store.setItem(v2StorageKey(logicalKey), value);
}

export function removeMigratedItem(store: Store, logicalKey: string): void {
  store.removeItem(v2StorageKey(logicalKey));
  store.removeItem(legacyMd5Key(logicalKey));
}
